import { Button, Dropdown, notification } from 'antd'
import { GlobalOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES } from '../../i18n'
import styled from 'styled-components'
import { configureLangHeader } from '../../libs/services'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getStaleTime, QUERY_TYPES } from '@/config/reactQuery'
import SettingsService from '@/libs/services/settings'
import { useEffect, useCallback } from 'react'

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation()
  const queryClient = useQueryClient()

  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => SettingsService.getMe(),
    staleTime: getStaleTime(QUERY_TYPES.USER),
  })

  const userLanguage = userData?.data?.language

  // Mutation para actualizar el idioma en el backend
  const updateLanguageMutation = useMutation({
    mutationFn: language => SettingsService.updateMe({ language }),
    onSuccess: data => {
      const newLanguage = data.data.language
      i18n.changeLanguage(newLanguage)
      configureLangHeader(newLanguage)
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: ['me'], exact: true })
      /* 
            notification.success({
              message: t('common.languageUpdated'),
              description: t('common.languageUpdatedDescription'),
              placement: 'topRight',
              duration: 3,
            }) */
    },
    onError: error => {
      console.error('Error updating language:', error)

      // Revert to previous language on error
      const currentLanguage = userLanguage || i18n.language
      i18n.changeLanguage(currentLanguage)
      configureLangHeader(currentLanguage)

      notification.error({
        message: t('common.languageUpdateError'),
        description: t('common.languageUpdateErrorDescription'),
        placement: 'topRight',
        duration: 4,
      })
    },
  })

  // Validar y establecer el idioma del usuario cuando esté disponible
  useEffect(() => {
    if (userLoading) return // No hacer nada mientras carga

    const currentLanguage = i18n.language
    const targetLanguage = userLanguage || currentLanguage

    // Validar que el idioma esté soportado
    if (SUPPORTED_LANGUAGES.includes(targetLanguage) && currentLanguage !== targetLanguage) {
      i18n.changeLanguage(targetLanguage)
      configureLangHeader(targetLanguage)
    }
  }, [userLanguage, userLoading, i18n])

  const handleLanguageChange = useCallback(
    async lang => {
      // Validar que el idioma esté soportado
      if (!SUPPORTED_LANGUAGES.includes(lang)) {
        notification.error({
          message: t('common.unsupportedLanguage'),
          placement: 'topRight',
          duration: 3,
        })
        return
      }

      // Cambiar idioma en i18n inmediatamente para mejor UX
      i18n.changeLanguage(lang)
      configureLangHeader(lang)

      // Si hay usuario loggeado y el idioma es diferente, actualizar en el backend
      if (userData?.data && userLanguage !== lang) {
        updateLanguageMutation.mutate(lang)
      }
    },
    [i18n, userData, userLanguage, updateLanguageMutation, t]
  )

  // Obtener el idioma actual para mostrar
  const getCurrentLanguage = () => {
    if (userLoading) return i18n.language?.toUpperCase()
    return (userLanguage || i18n.language)?.toUpperCase()
  }

  const items = SUPPORTED_LANGUAGES.map(lang => ({
    key: lang,
    label: lang.toUpperCase(),
    onClick: () => handleLanguageChange(lang),
  }))

  return (
    <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
      <StyledButton
        type="text"
        icon={<GlobalOutlined />}
        loading={updateLanguageMutation.isPending || userLoading}
      >
        {getCurrentLanguage()}
      </StyledButton>
    </Dropdown>
  )
}

export default LanguageSwitcher
